import { useState, forwardRef, useImperativeHandle } from "react";
import {
  Attachment,
  isManualValveReport,
  isSafetyValveReport,
  ManualValveReport,
  SafetyValveReport,
} from "@/reports/types/SafetyValveReport";
import { Button, message } from "antd";
import useNetwork from "@/stores/network";
import useBoolean from "@/hooks/useBoolean";
import { useParams } from "react-router-dom";
import useRole from "@/auth/hooks/useRole";
import { getManualValveReport, getSafetyValveReport } from "@/reports/api";
import { ReportType } from "@/reports/types/Api";
import { getImagesOrientations } from "@/reports/utils/pdf";
import { Location } from "@/reports/config/locations";
import PdfModal from "./PdfModal";
import ImageAttachmentWarningModal from "./ImageAttachmentWarningModal";

interface ExportPdfButtonProps {
  displayButton?: boolean;
  reportType?: ReportType;
}

export interface ExportPdfButtonRef {
  triggerExport: () => void;
}

const ExportPdfButton = forwardRef<ExportPdfButtonRef, ExportPdfButtonProps>(
  ({ displayButton = true, reportType = "SAFETY_VALVE" }, ref) => {
    const { online } = useNetwork();
    const { isViewer } = useRole();
    const { location, id } = useParams();

    const {
      value: showPdfModal,
      setTrue: setPdfModalOpen,
      setFalse: setPdfModalClose,
    } = useBoolean(false);
    const {
      value: isLoadingLiveData,
      setTrue: setIsLoadingLiveData,
      setFalse: setIsNotLoadingLiveData,
    } = useBoolean(false);

    const [liveData, setLiveData] = useState<
      SafetyValveReport | ManualValveReport | undefined
    >(undefined);

    const { value: showWarningModal, setValue: setShowWarningModal } =
      useBoolean(false);

    const [imageAttachments, setImageAttachments] = useState<Attachment[]>([]);
    const [imageOrientations, setImageOrientations] = useState<string[]>([]);

    const handleExportButtonClick = async () => {
      setShowWarningModal(true);
    };

    useImperativeHandle(ref, () => ({
      triggerExport: handleExportButtonClick,
    }));

    const handleExport = async (includeImage: boolean) => {
      setShowWarningModal(false);
      if (location && id) {
        setIsLoadingLiveData();

        try {
          let reportData: SafetyValveReport | ManualValveReport | undefined;
          if (reportType === "SAFETY_VALVE") {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const report: any = await getSafetyValveReport(
              {
                onServer: true,
                value: id,
              },
              location as Location,
              isViewer
            );

            reportData = report.data.data;
          } else if (reportType === "MANUAL_VALVE") {
            const report = await getManualValveReport(
              {
                onServer: true,
                value: id,
              },
              location as Location,
              isViewer
            );

            reportData = report.data.data;
          }

          let images = [];
          if (reportData && isSafetyValveReport(reportData)) {
            images =
              reportData.Attachments?.filter((item) =>
                item.FileType.includes("image")
              ) ?? [];
          } else if (reportData && isManualValveReport(reportData)) {
            images =
              (reportData.ManualValveRepairReportAttachments?.filter((item) =>
                item.FileType.includes("image")
              ) as unknown as Attachment[]) ?? [];
          }

          // If the user wants to include image attachments
          if (includeImage) {
            // Get image orientations
            const orientations = await getImagesOrientations(images);
            setImageOrientations(orientations);
            setImageAttachments(images);
          } else {
            setImageOrientations([]);
            setImageAttachments([]);
          }

          if (!reportData) {
            message.error("Failed to fetch report data, cannot export pdf");
          } else {
            setLiveData(reportData);
            setPdfModalOpen();
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error fetching report data:", error);
          message.error("Error fetching report data, cannot export pdf");
        } finally {
          setIsNotLoadingLiveData();
        }
      } else {
        message.error("Invalid location or report id, cannot export pdf");
      }
    };

    return (
      <>
        {displayButton && (
          <Button
            key="export"
            type="primary"
            onClick={handleExportButtonClick}
            disabled={!online}
            loading={isLoadingLiveData}
          >
            Export PDF
          </Button>
        )}
        <ImageAttachmentWarningModal
          visible={showWarningModal}
          onCancel={() => setShowWarningModal(false)}
          onExport={handleExport}
        />
        <PdfModal
          data={liveData}
          location={location}
          visible={showPdfModal}
          onClose={() => setPdfModalClose()}
          imageAttachments={imageAttachments}
          imageOrientations={imageOrientations}
          reportType={reportType}
        />
      </>
    );
  }
);

ExportPdfButton.displayName = "ExportPdfButton";

export default ExportPdfButton;
